import { useEffect, useRef } from "preact/hooks";
import type { RefObject } from "preact";
import type { Action } from "../state/actions.ts";
import type { Point, Tool } from "../types/document.ts";

function canvasPoint(e: MouseEvent, canvas: HTMLCanvasElement): Point {
  const rect = canvas.getBoundingClientRect();
  return [e.clientX - rect.left, e.clientY - rect.top];
}

export function useCanvasEvents(
  canvasRef: RefObject<HTMLCanvasElement | null>,
  dispatch: (action: Action) => void,
  activeTool: Tool,
  activeEmoji: string | undefined,
): void {
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const onDown = (e: MouseEvent) => {
      if (activeTool === "text") return;
      const pt = canvasPoint(e, canvas);
      if (activeTool === "emoji") {
        if (activeEmoji) dispatch({ type: "STAMP_EMOJI", point: pt });
        return;
      }
      drawing.current = true;
      dispatch({ type: "DRAW_START", point: pt });
    };

    const onMove = (e: MouseEvent) => {
      if (!drawing.current) return;
      dispatch({ type: "DRAW_MOVE", point: canvasPoint(e, canvas) });
    };

    const onUp = () => {
      if (!drawing.current) return;
      drawing.current = false;
      dispatch({ type: "DRAW_END" });
    };

    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      canvas.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [canvasRef, dispatch, activeTool, activeEmoji]);
}
